(() => {
  if (customElements.get('ozy-category-rail')) return;
  customElements.define('ozy-category-rail', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.track = this.querySelector('[data-category-track]');
      this.cards = [...this.track.children];
      if (this.cards.length < 2) return;
      // Repeat visual cards around the original, keyboard-accessible set.
      const copy = card => {
        const clone = card.cloneNode(true);
        clone.dataset.railClone = '';
        clone.removeAttribute('data-shopify-editor-block');
        clone.setAttribute('aria-hidden', 'true');
        clone.tabIndex = -1;
        clone.querySelectorAll('img').forEach(image => image.draggable = false);
        return clone;
      };
      this.track.prepend(...this.cards.map(copy));
      this.track.append(...this.cards.map(copy));
      this.cards.forEach(card => card.querySelectorAll('img').forEach(image => image.draggable = false));
      this.measure();
      for (const [selector, direction] of [['[data-rail-previous]', -1], ['[data-rail-next]', 1]]) {
        const button = this.querySelector(selector);
        if (!button) continue;
        button.hidden = false;
        button.addEventListener('click', () => {
          const step = this.cards[1].offsetLeft - this.cards[0].offsetLeft;
          this.glide(Math.round((this.track.scrollLeft + direction * step) / step) * step);
        }, options);
      }
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this.track);
      this.track.addEventListener('scroll', () => this.wrap(), {...options, passive:true});
      this.track.addEventListener('dragstart', event => event.preventDefault(), options);
      this.track.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.stopGlide();
        this.drag = {id:event.pointerId, x:event.clientX, left:this.track.scrollLeft, lastX:event.clientX, time:performance.now(), velocity:0};
        this.moved = false;
      }, options);
      this.track.addEventListener('pointermove', event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const distance = event.clientX - this.drag.x;
        if (!this.moved && Math.abs(distance) < 6) return;
        this.moved = true;
        this.track.classList.add('is-dragging');
        this.track.setPointerCapture(event.pointerId);
        const now = performance.now(), elapsed = now - this.drag.time;
        if (elapsed > 0) this.drag.velocity = this.drag.velocity * .35 + (this.drag.lastX - event.clientX) / elapsed * .65;
        this.drag.lastX = event.clientX;
        this.drag.time = now;
        this.track.scrollLeft = this.drag.left - distance;
      }, options);
      const release = event => {
        if (!this.drag) return;
        const drag = this.drag;
        this.drag = null;
        if (this.track.hasPointerCapture(drag.id)) this.track.releasePointerCapture(drag.id);
        if (this.moved && event.type !== 'pointercancel') {
          const step = this.cards[1].offsetLeft - this.cards[0].offsetLeft;
          const velocity = performance.now() - drag.time < 100 ? drag.velocity : 0;
          const momentum = Math.max(-step * 3, Math.min(step * 3, velocity * 220));
          const target = Math.round((this.track.scrollLeft + momentum) / step) * step;
          this.glide(target);
        }
        this.track.classList.remove('is-dragging');
      };
      this.track.addEventListener('pointerup', release, options);
      this.track.addEventListener('pointercancel', release, options);
      this.track.addEventListener('lostpointercapture', release, options);
      this.track.addEventListener('wheel', () => this.stopGlide(), {...options, passive:true});
      this.track.addEventListener('touchstart', () => this.stopGlide(), {...options, passive:true});
      this.track.addEventListener('click', event => {
        if (this.moved) {event.preventDefault(); event.stopPropagation(); this.moved = false;}
      }, {...options, capture:true});
      this.track.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const step = this.cards[1].offsetLeft - this.cards[0].offsetLeft;
        this.track.scrollBy({left:step * (event.key === 'ArrowRight' ? 1 : -1), behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('.ozy-home-categories__card');
        if (card) card.scrollIntoView({block:'nearest', inline:'center', behavior:'smooth'});
      }, options);
    }
    stopGlide() {
      cancelAnimationFrame(this.frame);
      this.track.classList.remove('is-settling');
    }
    glide(target) {
      this.stopGlide();
      const start = this.track.scrollLeft, distance = target - start;
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced || Math.abs(distance) < 1) {this.track.scrollLeft = target; this.wrap(); return;}
      this.track.classList.add('is-settling');
      const started = performance.now(), duration = Math.min(780, 480 + Math.abs(distance) * .18);
      const tick = now => {
        const progress = Math.min(1, (now - started) / duration);
        let position = start + distance * (1 - Math.pow(1 - progress, 3));
        while (position < this.period * .5) position += this.period;
        while (position > this.period * 2) position -= this.period;
        this.track.scrollLeft = position;
        if (progress < 1) this.frame = requestAnimationFrame(tick);
        else this.track.classList.remove('is-settling');
      };
      this.frame = requestAnimationFrame(tick);
    }
    measure() {
      const period = this.cards[0].getBoundingClientRect().left - this.track.children[0].getBoundingClientRect().left;
      if (!period) return;
      if (Math.abs(period - (this.period || 0)) < 1) return;
      const phase = this.period ? ((this.track.scrollLeft / this.period) % 1) : 0;
      this.period = period;
      this.track.scrollLeft = period * (1 + phase);
    }
    wrap() {
      if (!this.period) return;
      const left = this.track.scrollLeft;
      const shift = left < this.period * .5 ? this.period : left > this.period * 2 ? -this.period : 0;
      if (!shift) return;
      this.track.scrollLeft += shift;
      if (this.drag) this.drag.left += shift;
    }
    disconnectedCallback() {
      this.stopGlide();
      this.controller?.abort(); this.controller = null;
      this.resize?.disconnect();
      this.querySelectorAll('[data-rail-clone]').forEach(card => card.remove());
    }
  });
})();
